import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, FloppyDisk, Stack, Plus, ArrowSquareOut, BookOpenText, Sparkle, Archive,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TIERS = [
  { id: 1, name: "Anchor", caption: "Always-on context · foundational", icon: Sparkle, accent: "#4A5D4E" },
  { id: 2, name: "Logic Core", caption: "Semantic RAG · mid-depth", icon: Stack, accent: "#1C1B1A" },
  { id: 3, name: "Archive", caption: "Semantic RAG · deep reference", icon: Archive, accent: "#6B6A68" },
];

// =====================================================================
// SiloPickerModal — shared picker for "Commit to silo" and "Send to staging".
//
// Phase 2 of the Side-Bump system. Creators only — caller is responsible for
// gating display, but the backend re-enforces.
//
// mode = "commit"  → pick silo + tier + title → POST /commit-from-conversation
// mode = "staging" → pick silo + label        → POST /send-to-staging
// =====================================================================
export default function SiloPickerModal({ open, mode, messages, defaultTitle, onClose, onSuccess }) {
  const [silos, setSilos] = useState([]);
  const [loadingSilos, setLoadingSilos] = useState(false);
  const [selectedSiloId, setSelectedSiloId] = useState("");
  const [tier, setTier] = useState(2);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [creatingSilo, setCreatingSilo] = useState(false);
  const [newSiloName, setNewSiloName] = useState("");

  const refresh = useCallback(async () => {
    setLoadingSilos(true);
    try {
      const r = await api.get("/silos");
      setSilos(r.data || []);
      if ((r.data || []).length === 1) setSelectedSiloId(r.data[0].id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load your silos.");
    } finally { setLoadingSilos(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    setTitle(defaultTitle || "");
    setSelectedSiloId("");
    setTier(2);
    setCreatingSilo(false);
    setNewSiloName("");
    refresh();
  }, [open, defaultTitle, refresh]);

  const selectedSilo = useMemo(() => silos.find((s) => s.id === selectedSiloId), [silos, selectedSiloId]);

  if (!open) return null;

  const createSilo = async () => {
    const name = newSiloName.trim();
    if (!name) return toast.error("Silo name required.");
    setBusy(true);
    try {
      // Use 'academic' template — same default the Dashboard's CreateSiloModal uses.
      // Backend validates template ∈ ('academic','tactical','modern').
      const r = await api.post("/silos", { name, template: "academic" });
      setSilos((prev) => [r.data, ...prev]);
      setSelectedSiloId(r.data.id);
      setCreatingSilo(false);
      setNewSiloName("");
      toast.success(`Silo "${r.data.name}" created.`);
    } catch (e) {
      // Reset creating state on error so the user sees the existing-silo list
      // again instead of getting stranded on an empty create form (iter-24 fix).
      setCreatingSilo(false);
      toast.error(e.response?.data?.detail || "Could not create silo.");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!selectedSiloId) return toast.error("Pick a silo first.");
    setBusy(true);
    try {
      if (mode === "commit") {
        const r = await api.post(`/silos/${selectedSiloId}/commit-from-conversation`, {
          tier, title: title.trim() || undefined, messages,
        });
        toast.success(`Committed to "${selectedSilo?.name}" · ${TIERS.find((t) => t.id === tier)?.name}`);
        onSuccess?.(r.data);
      } else {
        const r = await api.post(`/silos/${selectedSiloId}/send-to-staging`, {
          label: title.trim() || undefined, messages,
        });
        toast.success(`Sent to "${selectedSilo?.name}"'s staging area.`);
        onSuccess?.(r.data);
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not complete the action.");
    } finally { setBusy(false); }
  };

  const heading = mode === "commit" ? "Commit conversation to silo" : "Send conversation to staging";
  const helper = mode === "commit"
    ? "Adds the conversation as a permanent document in your silo's library at the tier you choose."
    : "Appends the conversation to your silo's Workbench staging buffer for later editing.";

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid={`silo-picker-modal-${mode}`}
      >
        <motion.div
          className="bg-white rounded-xl border hairline shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto"
          initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between p-6 pb-4">
            <div>
              <h3 className="font-serif-display text-2xl text-[#1C1B1A] flex items-center gap-2">
                {mode === "commit" ? <BookOpenText size={22} weight="duotone" className="text-[#4A5D4E]" /> : <Stack size={22} weight="duotone" className="text-[#4A5D4E]" />}
                {heading}
              </h3>
              <p className="text-xs text-[#6B6A68] mt-1 max-w-md">{helper}</p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[#F0F1EE]" data-testid="silo-picker-close">
              <X size={16} />
            </button>
          </div>

          <div className="px-6 pb-6 space-y-5">
            {/* Step 1 · pick silo (or create new) */}
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-2">Target silo</label>
              {loadingSilos && <div className="text-sm text-[#6B6A68]">Loading your silos…</div>}
              {!loadingSilos && silos.length === 0 && !creatingSilo && (
                <div className="p-4 border hairline rounded-md text-center bg-[#FAF8F2]" data-testid="silo-picker-empty">
                  <p className="text-sm text-[#6B6A68]">You don't have any silos yet.</p>
                  <button onClick={() => setCreatingSilo(true)} data-testid="silo-picker-create-first"
                    className="mt-2 text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] inline-flex items-center gap-1.5">
                    <Plus size={12} /> Create your first silo
                  </button>
                </div>
              )}
              {!loadingSilos && silos.length > 0 && !creatingSilo && (
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="silo-picker-list">
                  {silos.map((s) => (
                    <button key={s.id} onClick={() => setSelectedSiloId(s.id)}
                      data-testid={`silo-picker-row-${s.slug}`}
                      className={`w-full text-left p-3 rounded-md border transition-colors ${selectedSiloId === s.id ? "border-[#4A5D4E] bg-[#F0F4ED]" : "hairline bg-white hover:border-[#A89A7A]"}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-[#1C1B1A] truncate">{s.name}</div>
                          <div className="text-[11px] text-[#6B6A68] truncate">/s/{s.slug} · {s.template || "blank"}</div>
                        </div>
                        {selectedSiloId === s.id && <Sparkle size={14} weight="duotone" className="text-[#4A5D4E] shrink-0" />}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {creatingSilo && (
                <div className="p-3 border hairline rounded-md bg-[#FAF8F2] flex items-center gap-2" data-testid="silo-picker-create-form">
                  <input value={newSiloName} onChange={(e) => setNewSiloName(e.target.value)} autoFocus
                    placeholder="New silo name…"
                    onKeyDown={(e) => e.key === "Enter" && createSilo()}
                    data-testid="silo-picker-new-name"
                    className="flex-1 bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
                  <button onClick={createSilo} disabled={busy || !newSiloName.trim()} data-testid="silo-picker-confirm-create"
                    className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50">
                    Create
                  </button>
                  <button onClick={() => { setCreatingSilo(false); setNewSiloName(""); }} className="text-xs text-[#6B6A68] hover:text-[#1C1B1A]">
                    Cancel
                  </button>
                </div>
              )}
              {!creatingSilo && silos.length > 0 && (
                <button onClick={() => setCreatingSilo(true)} data-testid="silo-picker-create-toggle"
                  className="mt-2 text-xs text-[#4A5D4E] hover:underline inline-flex items-center gap-1">
                  <Plus size={11} /> Create new silo instead
                </button>
              )}
            </div>

            {/* Step 2 · tier picker (commit only) */}
            {mode === "commit" && (
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-2">Library tier</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="silo-picker-tiers">
                  {TIERS.map((t) => {
                    const Icon = t.icon;
                    const active = tier === t.id;
                    return (
                      <button key={t.id} onClick={() => setTier(t.id)}
                        data-testid={`silo-picker-tier-${t.id}`}
                        className={`text-left p-3 rounded-md border transition-colors ${active ? "border-[#4A5D4E] bg-[#F0F4ED]" : "hairline bg-white hover:border-[#A89A7A]"}`}
                      >
                        <div className="flex items-center gap-1.5">
                          <Icon size={14} weight="duotone" style={{ color: t.accent }} />
                          <span className="text-sm font-medium text-[#1C1B1A]">{t.name}</span>
                        </div>
                        <div className="text-[10px] text-[#6B6A68] mt-1 leading-tight">{t.caption}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 3 · title / label */}
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-2">
                {mode === "commit" ? "Document title (optional)" : "Staging label (optional)"}
              </label>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder={defaultTitle || "Defaults to first 7 words of conversation"}
                data-testid="silo-picker-title"
                className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
            </div>

            {/* Submit */}
            <div className="flex items-center justify-between gap-3 pt-2">
              <span className="text-[11px] text-[#6B6A68]">
                {messages?.length || 0} message{(messages?.length || 0) === 1 ? "" : "s"} will be {mode === "commit" ? "committed" : "sent"}.
              </span>
              <div className="flex gap-2">
                <button onClick={onClose} data-testid="silo-picker-cancel"
                  className="text-sm px-4 py-2 rounded-md text-[#6B6A68] hover:text-[#1C1B1A]">Cancel</button>
                <button onClick={submit} disabled={busy || !selectedSiloId}
                  data-testid="silo-picker-submit"
                  className="text-sm px-5 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
                  {mode === "commit" ? <BookOpenText size={14} weight="duotone" /> : <ArrowSquareOut size={14} />}
                  {busy ? "Working…" : (mode === "commit" ? "Commit to silo" : "Send to staging")}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
